<template>
  <div class="processing-page">
    <!-- Animated Background -->
    <div class="bg-decoration">
      <div class="circle c1"></div>
      <div class="circle c2"></div>
      <div class="circle c3"></div>
    </div>

    <!-- Content -->
    <div class="processing-content">
      <!-- Loading Animation -->
      <div class="loading-animation">
        <div class="spinner"></div>
        <div class="payment-icon-wrapper">
          <img 
            v-if="method === 'alipay' || method === 'alipay_direct'" 
            src="https://img.icons8.com/color/96/alipay.png" 
            class="method-icon"
          />
          <img 
            v-else-if="method === 'wechat'" 
            src="https://img.icons8.com/color/96/weixing.png" 
            class="method-icon"
          />
        </div>
      </div>

      <!-- Status Text -->
      <div class="status-text">
        <div class="main-text">{{ statusText }}</div>
        <div class="sub-text">{{ subText }}</div>
      </div>

      <!-- Amount -->
      <div class="amount-display">
        <span class="currency">¥</span>
        <span class="value">{{ amount }}</span>
      </div>

      <!-- Progress Steps -->
      <div class="progress-steps">
        <div class="step" :class="{ active: step >= 1, done: step > 1 }">
          <div class="step-dot"></div>
          <span>创建订单</span>
        </div>
        <div class="step-line" :class="{ active: step >= 2 }"></div>
        <div class="step" :class="{ active: step >= 2, done: step > 2 }">
          <div class="step-dot"></div>
          <span>连接支付</span>
        </div>
        <div class="step-line" :class="{ active: step >= 3 }"></div>
        <div class="step" :class="{ active: step >= 3 }">
          <div class="step-dot"></div>
          <span>等待支付</span>
        </div>
      </div>

      <!-- Status Text for Checking -->
      <div v-if="isChecking" class="checking-text">
        正在检查支付状态...
      </div>

      <!-- Already Paid Button -->
      <button class="paid-btn" @click="manualCheckPayment" :disabled="isChecking">
        已经支付
      </button>
      
      <!-- Cancel Button -->
      <button class="cancel-btn" @click="cancelPayment">取消支付</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentProcessing',
  data() {
    return {
      method: 'alipay',
      amount: 0,
      type: '',
      videoId: null,
      referenceNo: '',  // 订单号，用于轮询状态
      pollingTimer: null,  // 轮询定时器
      isChecking: false,   // 是否正在检查状态

      step: 1,
      statusText: '正在创建订单...',
      subText: '请稍候'
    }
  },
  created() {
    this.method = this.$route.query.method || 'alipay';
    this.amount = this.$route.query.amount || 0;
    this.type = this.$route.query.type || 'single';
    this.videoId = this.$route.query.videoId || null;

  },
  mounted() {
    // 检查是否有待处理的支付（从支付宝返回的情况）
    const pending = localStorage.getItem('pendingPayment');
    if (pending) {
      const data = JSON.parse(pending);
      // 如果是5分钟内的订单，恢复轮询
      if (Date.now() - data.time < 5 * 60 * 1000) {
        this.referenceNo = data.referenceNo;
        this.amount = data.amount;
        this.videoId = data.videoId;
        this.type = data.type;
        this.step = 3;
        this.statusText = '正在确认支付结果...';
        this.subText = '请稍候，正在查询支付状态';
        this.startPolling();
        return; // 不重新创建订单
      } else {
        localStorage.removeItem('pendingPayment');
      }
    }
    
    this.startPaymentFlow();
  },
  beforeDestroy() {
    // 清理定时器
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    async startPaymentFlow() {
      try {
        // Step 1: Creating order
        this.step = 1;
        this.statusText = '正在创建订单...';
        await this.delay(800);

        // Step 2: Connecting to payment
        this.step = 2;
        this.statusText = '正在连接支付通道...';
        await this.delay(500);

        // Prepare request body
        const userStr = localStorage.getItem('user');
        let requestBody = {
          type: this.type,
          amount: this.amount,
          videoId: this.videoId,

          paymentMethod: this.method // 传递支付方式
        };

        if (userStr) {
          const user = JSON.parse(userStr);
          requestBody.username = user.username;
        } else {
          let deviceId = localStorage.getItem('deviceId');
          if (!deviceId) {
            deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('deviceId', deviceId);
          }
          requestBody.deviceId = deviceId;
        }

        const promoterId = localStorage.getItem('promoterId');
        if (promoterId) requestBody.promoterId = promoterId;

        // 使用 Direct Server API 模式 (根据 Demo-ALIPAY.PHP)
        const response = await this.$http.post('/direct/create', {
          ...requestBody,
          // 不传递视频标题作为产品信息
          paymentMethod: this.method === 'wechat' ? 'wechat' : 'alipay'
        });

        const result = response.data;

        // 检查响应状态
        if (result.code !== 0) {
          throw new Error(result.message || '创建订单失败');
        }

        const data = result.data;
        
        // 保存订单号用于轮询
        this.referenceNo = data.referenceNo;
        // 存储到 localStorage 以便页面刷新后恢复
        localStorage.setItem('pendingPayment', JSON.stringify({
          referenceNo: data.referenceNo,
          amount: this.amount,
          videoId: this.videoId,
          type: this.type,
          time: Date.now()
        }));

        // Step 3: Redirecting to payment gateway
        this.step = 3;
        this.statusText = '正在跳转支付页面...';
        this.subText = '请在支付页面完成付款';
        
        // 启动轮询（在跳转前启动，这样返回后会继续检测）
        this.startPolling();
        
        await this.delay(500);

        // 直接跳转到支付宝支付页面
        if (data.requestURL) {
          console.log('📤 跳转到支付URL:', data.requestURL);
          window.location.href = data.requestURL;
        } else {
          throw new Error('未获取到支付链接');
        }

      } catch (e) {
        console.error('Payment error:', e);
        this.$toast.fail(e.response?.data?.message || e.message || '支付失败');
        setTimeout(() => {
          this.$router.back();
        }, 2000);
      }
    },
    /**
     * 创建隐藏表单并提交到 Fiuu 网关
     * Direct Server API 模式核心方法
     */
    submitToGateway(url, params) {
      console.log('📤 提交到支付网关:', url, params);
      
      // 创建隐藏表单
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url;
      form.style.display = 'none';
      
      // 添加所有支付参数
      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      
      // 添加到页面并提交
      document.body.appendChild(form);
      form.submit();
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    cancelPayment() {
      localStorage.removeItem('pendingPayment');
      if (this.pollingTimer) clearInterval(this.pollingTimer);
      this.$router.back();
    },
    // 启动轮询检测支付状态
    startPolling() {
      // 监听页面可见性变化（用户从支付宝返回时触发）
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      
      // 每3秒轮询一次
      this.pollingTimer = setInterval(() => {
        this.checkPaymentStatus();
      }, 3000);
      
      // 立即检查一次
      this.checkPaymentStatus();
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible' && this.referenceNo) {
        console.log('页面可见，检查支付状态...');
        this.checkPaymentStatus();
      }
    },
    async checkPaymentStatus() {
      if (!this.referenceNo) return;
      
      try {
        const { data } = await this.$http.get(`/fiuu/status/${this.referenceNo}`);
        console.log('支付状态查询结果:', data);
        
        if (data.status === 'completed') {
          // 支付成功！
          clearInterval(this.pollingTimer);
          localStorage.removeItem('pendingPayment');
          
          this.$toast.success('支付成功！正在跳转播放...');
          
          // 直接跳转到播放页面开始播放
          this.$router.replace({
            path: '/player',
            query: {
              id: this.videoId
            }
          });
        }
      } catch (e) {
        console.log('状态查询失败:', e.message);
      }
    },
    // 手动检查支付状态（用户点击"已经支付"按钮）
    async manualCheckPayment() {
      if (!this.referenceNo) {
        // 尝试从 localStorage 恢复
        const pending = localStorage.getItem('pendingPayment');
        if (pending) {
          const data = JSON.parse(pending);
          this.referenceNo = data.referenceNo;
          this.videoId = data.videoId;
        }
      }
      
      if (!this.referenceNo) {
        this.$toast.fail('未找到订单信息，请重新购买');
        return;
      }
      
      this.isChecking = true;
      this.statusText = '正在检查支付状态...';
      
      try {
        const { data } = await this.$http.get(`/fiuu/status/${this.referenceNo}`);
        console.log('手动检查支付状态:', data);
        
        if (data.status === 'completed') {
          localStorage.removeItem('pendingPayment');
          this.$toast.success('支付成功！正在跳转播放...');
          this.$router.replace({
            path: '/player',
            query: { id: this.videoId }
          });
        } else if (data.status === 'pending') {
          this.$toast('支付尚未完成，请稍后再试');
          this.statusText = '等待支付确认...';
        } else {
          this.$toast.fail('支付状态: ' + data.status);
        }
      } catch (e) {
        this.$toast.fail('检查失败，请稍后重试');
      } finally {
        this.isChecking = false;
      }
    }
  }
}
</script>

<style scoped>
.processing-page {
  background: linear-gradient(180deg, #1a1a2e 0%, #0f0f23 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

/* Decorative Background Circles */
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
}

.c1 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  top: -100px;
  left: -100px;
  animation: float 6s ease-in-out infinite;
}

.c2 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #ff6b35, #f7931e);
  bottom: 100px;
  right: -50px;
  animation: float 8s ease-in-out infinite reverse;
}

.c3 {
  width: 150px;
  height: 150px;
  background: #4CAF50;
  bottom: -50px;
  left: 50px;
  animation: float 5s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

.processing-content {
  text-align: center;
  z-index: 10;
  padding: 20px;
}

/* Loading Animation */
.loading-animation {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 30px;
}

.spinner {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid rgba(255,255,255,0.1);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.payment-icon-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.method-icon {
  width: 40px;
  height: 40px;
}

/* Status Text */
.status-text {
  color: white;
  margin-bottom: 20px;
}

.main-text {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
}

.sub-text {
  font-size: 14px;
  color: #888;
}

/* Amount Display */
.amount-display {
  color: #ff6b35;
  margin-bottom: 40px;
}

.currency {
  font-size: 20px;
}

.value {
  font-size: 36px;
  font-weight: bold;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #666;
  font-size: 12px;
}

.step.active {
  color: #fff;
}

.step-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #333;
  margin-bottom: 8px;
  transition: all 0.3s;
}

.step.active .step-dot {
  background: #667eea;
  box-shadow: 0 0 10px rgba(102, 126, 234, 0.5);
}

.step.done .step-dot {
  background: #4CAF50;
}

.step-line {
  width: 40px;
  height: 2px;
  background: #333;
  margin: 0 10px 20px;
  transition: all 0.3s;
}

.step-line.active {
  background: linear-gradient(90deg, #667eea, #764ba2);
}

/* Cancel Button */
.cancel-btn {
  background: transparent;
  border: 1px solid #666;
  color: #888;
  padding: 10px 30px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
}

.cancel-btn:hover {
  border-color: #999;
  color: #ccc;
}

/* Paid Button (Already Paid) */
.paid-btn {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  border: none;
  color: white;
  padding: 12px 40px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin-bottom: 15px;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
  transition: all 0.3s;
}

.paid-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

.paid-btn:disabled {
  background: #666;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Checking Text */
.checking-text {
  color: #4CAF50;
  font-size: 14px;
  margin-bottom: 10px;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
